import { Module } from 'vuex';
import { apiHttpClient } from '../../app/app.service';
import { RootState } from '../../app/app.store';
import { User } from '../../user/show/user-show.store';
import { postFileProcess } from '../post.service';
import appRouter from '@/app/app.router';

export interface Post {
  id: string;
  title: string;
  content: string;
  user: User;
  totalComments: number;
  totalLikes: number;
  liked: number;
  series: any;
  completed: string;
  file: {
    id: number;
    width: number;
    height: number;
    orientation: string;
    size: {
      thumbnail: string;
      medium: string;
      large: string;
    };
  };
  tags: [
    {
      id: number;
      name: string;
    },
  ];
}

export interface NodeItem {
  id: string;
  title: string;
  nodeIndex: number;
  duration: string;
}

export interface PostShowStoreState {
  loading: boolean;
  post: Post;
  layout: string;
  playList: {
    name: string;
    nodes: Array<NodeItem>;
    source: any;
  };
}

export const postShowStoreModule: Module<PostShowStoreState, RootState> = {
  namespaced: true,

  state: {
    loading: false,
    post: {},
    layout: '',
    playList: {},
  } as PostShowStoreState,

  getters: {
    loading(state) {
      return state.loading;
    },

    post(state) {
      return Object.keys(state.post).length ? state.post : null;
    },

    layout(state) {
      return state.layout;
    },

    currentPostIndex(state, _, rootState) {
      return state.playList.nodes.findIndex(item => item.id === state.post.id);
    },

    prevPost(state, getters, rootState) {
      return state.playList.nodes[getters.currentPostIndex - 1];
    },

    nextPost(state, getters, rootState) {
      return state.playList.nodes[getters.currentPostIndex + 1];
    },

    canNavigateBack(state, getters, rootState) {
      return state.playList.nodes.length > 0 && getters.currentPostIndex !== 0;
    },

    canNavigateForward(state, getters, rootState) {
      return (
        state.playList.nodes.length > 0 &&
        state.playList.nodes.length !== getters.currentPostIndex + 1
      );
    },

    canGetMorePosts(_, getters, rootState, rootGetters) {
      return (
        rootGetters['post/index/hasMore'] &&
        rootState.post.index.posts.length - getters.currentPostIndex < 3
      );
    },

    playList(state) {
      return state.playList;
    },
  },

  mutations: {
    setLoading(state, data) {
      state.post = data;
    },

    setPost(state, data) {
      state.post = data;
    },

    setLayout(state, data) {
      state.layout = data;
    },

    setPostLiked(state, data) {
      const { postId, liked } = data;

      if (state.post.id === postId) {
        state.post.liked = liked;
      }
    },

    setCompleted(state, data) {
      const { nodeId, completed } = data;

      if (state.post.id === nodeId) {
        state.post.completed = completed;
      }
    },

    setPostTotalLikes(state, data) {
      const { postId, actionType } = data;

      if (state.post.id === postId) {
        switch (actionType) {
          case 'increase':
            state.post.totalLikes++;
            break;
          case 'decrease':
            state.post.totalLikes--;
            break;
        }
      }
    },

    setPostTotalComments(state, data) {
      const { postId, actionType } = data;

      if (state.post.id === postId) {
        switch (actionType) {
          case 'increase':
            state.post.totalComments++;
            break;
          case 'decrease':
            state.post.totalComments--;
            break;
        }
      }
    },

    setPlayList(state, data) {
      state.playList = data;
    },
  },

  actions: {
    async getPostById({ commit }, postId) {
      commit('setLoading', true);

      try {
        const response = await apiHttpClient.get(`/nodes/${postId}`);
        commit('setLoading', false);
        commit('setPost', response.data);

        return response;
      } catch (error) {
        commit('setLoading', false);

        throw error.response;
      }
    },

    async goGetPrevPost({ getters, dispatch }) {
      if (!getters.canNavigateBack) return;

      try {
        const prevPostId = getters.prevPost.id;
        const response = await dispatch('getPostById', prevPostId);

        appRouter.replace({
          name: 'postShow',
          params: { postId: prevPostId },
        });

        return response;
      } catch (error) {
        throw error.response;
      }
    },

    async goGetNextPost({ getters, dispatch }) {
      if (!getters.canNavigateForward) return;

      if (getters.canGetMorePosts) {
        dispatch('post/index/getPosts', {}, { root: true });
      }

      try {
        const nextPostId = getters.nextPost.id;
        const response = await dispatch('getPostById', nextPostId);

        appRouter.replace({
          name: 'postShow',
          params: { postId: nextPostId },
        });

        return response;
      } catch (error) {
        throw error.response;
      }
    },

    // 加载播放列表
    async loadPlayList({ state, dispatch, commit }) {
      // 如果没特别设置播放列表名称
      if (
        (state.post && state.post.series && !state.playList.name) ||
        state.playList.name === 'series'
      ) {
        let series: any;

        // 查找 seriesCache
        series = await dispatch(
          'series/show/getSeriesCache',
          state.post.series.id,
          { root: true },
        );

        if (!series) {
          // 如果没有 seriesCache，请求 series
          const response = await dispatch(
            'series/show/getSeriesById',
            state.post.series.id,
            {
              root: true,
            },
          );

          series = response.data;
        }

        commit('series/show/setSeries', series, { root: true });

        // 提取 series 里所有 nodes 作为播放列表;
        const nodes = series.packs
          .map((pack: any) => {
            return pack.nodes.map((node: any) => node);
          })
          .flat();

        commit('setPlayList', { name: 'series', nodes });
      }
    },
  },
};
